import { useMemo } from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createLink, useSearch } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { startCase } from 'es-toolkit/compat';

import { performanceMonitoringFlagsDescription } from '../common';
import { PerformanceMonitoring } from '../types';

import { platformTerminologies, usePlatform } from '@/features/users';
import { Button } from '@/shared/components/button';
import { FlagTriangleRight, Info } from '@/shared/components/icons';
import { Tooltip } from '@/shared/components/tooltip';

const columnHelper = createColumnHelper<PerformanceMonitoring>();

const LinkButton = createLink(Button);

const usePerformanceMonitoringColumns = () => {
  const platform = usePlatform();
  const adUnit = platformTerminologies.adUnit[platform];
  const adUnitTitle = startCase(adUnit.singular);

  const search = useSearch({ strict: false });

  const columns = useMemo(
    () => [
      columnHelper.accessor('adGroupName', {
        header: () => (
          <Typography variant='body3' component='span'>
            {`${adUnitTitle} Name`}
          </Typography>
        ),
        cell: (props) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
            whiteSpace='normal'
          >
            {props.getValue()}
          </Typography>
        ),
        size: 280,
      }),

      columnHelper.accessor('adGroupId', {
        header: () => (
          <Typography variant='body3' component='span'>
            {`${adUnitTitle} ID`}
          </Typography>
        ),
        cell: (props) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
          >
            {props.getValue()}
          </Typography>
        ),
        size: 140,
      }),

      columnHelper.accessor('flags', {
        header: () => (
          <Stack direction='row' alignItems='center' gap={0.5}>
            <Tooltip
              title={`Flags indicate ${adUnit.plural} that need attention`}
              placement='top'
            >
              <Info sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Tooltip>
            <Typography variant='body3' component='span'>
              Flag
            </Typography>
          </Stack>
        ),
        cell: (props) => {
          const flags = props.getValue();

          if (flags.length === 0) {
            return (
              <Typography
                variant='subtitleNumbers'
                component='span'
                color='text.secondary'
              >
                None
              </Typography>
            );
          }

          return (
            <>
              {flags.map((flag) => (
                <Tooltip
                  title={
                    Object.prototype.hasOwnProperty.call(
                      performanceMonitoringFlagsDescription,
                      flag
                    )
                      ? performanceMonitoringFlagsDescription[
                          flag as keyof typeof performanceMonitoringFlagsDescription
                        ]
                      : 'Description not available'
                  }
                  key={flag}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    gap={0.5}
                    sx={{ mb: 0.5 }}
                  >
                    <FlagTriangleRight
                      sx={{ fontSize: 18, color: 'error.main' }}
                    />
                    <Typography
                      variant='subtitleNumbers'
                      component='span'
                      color='error.main'
                      noWrap
                    >
                      {flag}
                    </Typography>
                  </Stack>
                </Tooltip>
              ))}
            </>
          );
        },
        size: 280,
      }),

      columnHelper.accessor('cpm', {
        enableSorting: false,
        header: () => (
          <Typography variant='body3' component='span'>
            CPM
          </Typography>
        ),
        cell: (props) => {
          const cpm = props.getValue();

          if (cpm === null) {
            return (
              <Typography
                variant='subtitleNumbers'
                component='span'
                color='text.secondary'
              >
                None
              </Typography>
            );
          }

          return (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.tertiary'
            >
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 2,
              }).format(cpm)}
            </Typography>
          );
        },
        size: 100,
      }),

      columnHelper.display({
        id: 'actions',
        header: () => (
          <Typography variant='body3' component='span'>
            Actions
          </Typography>
        ),
        cell: (props) => (
          <LinkButton
            to='.'
            search={{
              ...search,
              opt: 'diag',
              iex: 'vol',
              diag: 'detail',
              adGroups: [props.row.original.adGroupId],
            }}
          >
            Detailed Diagnostics
          </LinkButton>
        ),
      }),
    ],
    [adUnit, adUnitTitle, search]
  );

  return columns;
};

export default usePerformanceMonitoringColumns;
